import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowUp, Bot, Mic, Square } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Logo } from '@/components/brand/logo';
import { GoogleButton } from '@/components/google-button';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button, Field, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { currentLocale, LOCALES, setLocale, type Locale } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { setupApi } from './setup-api';
import { useSpeechInput } from './use-speech-input';

/* ---------------------------------------------------------------------------
   ההרשמה כשיחה. לפני שיש חשבון אין עדיין עוזר (אין עסק, אין תקציב AI), אז
   השיחה כאן קבועה: שפה ← שם העסק ← השם שלך ← אימייל וסיסמה בכרטיס. את התחום
   לא שואלים — העוזר מזהה אותו אחר כך מהמסמכים. עם פתיחת החשבון אותו מסך
   ממשיך לשיחה עם העוזר (AssistantSetup).

   פתיחת החשבון: "המשך עם Google" (ברירת המחדל), או אימייל וסיסמה בכרטיס —
   לא כהודעה, כדי שסיסמה לא תישמר בשיחה. היציאה ל-Google מאבדת את ה-state,
   אז מה שנאסף נשמר ב-sessionStorage וחוזר עם הכרטיס (?google_ticket=).
   --------------------------------------------------------------------------- */

type Step = 'language' | 'business' | 'owner' | 'account';

/** סיסמה: כמו PASSWORD_RULE בשרת. */
const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{12,128}$/;

const DRAFT_KEY = 'cm_signup_draft';
interface Draft {
  businessName: string;
  ownerName: string;
  language: Locale;
}

function loadDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    const d = raw ? (JSON.parse(raw) as Partial<Draft>) : null;
    return d && typeof d.businessName === 'string' && typeof d.ownerName === 'string' && (d.language === 'he' || d.language === 'en')
      ? (d as Draft)
      : null;
  } catch {
    return null;
  }
}
function saveDraft(d: Draft | null): void {
  try {
    if (d) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    else sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // מצב פרטי — ההרשמה עם Google תתחיל את השיחה מחדש, ולא יותר מזה.
  }
}

export function SignupChat() {
  const { t } = useTranslation(['onboarding', 'common']);
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  // חזרה מ-Google: השיחה ממשיכה מכרטיס החשבון, עם מה שנאסף לפני היציאה.
  const [returned] = useState(() =>
    search.google_ticket || search.google_error ? loadDraft() : null,
  );
  const [step, setStep] = useState<Step>(returned ? 'account' : 'language');
  const [language, setLanguage] = useState<Locale | null>(returned?.language ?? null);
  const [businessName, setBusinessName] = useState(returned?.businessName ?? '');
  const [ownerName, setOwnerName] = useState(returned?.ownerName ?? '');
  const [googleTicket] = useState(returned ? (search.google_ticket ?? null) : null);
  const [googleError] = useState(() =>
    search.google_error
      ? t(search.google_error === 'cancelled' ? 'chatSignup.googleCancelled' : 'chatSignup.googleFailed')
      : search.google_ticket && !returned
        ? t('chatSignup.googleLost')
        : null,
  );
  const bottom = useRef<HTMLDivElement>(null);

  // הכרטיס לא נשאר בכתובת (היסטוריה, צילום מסך).
  useEffect(() => {
    if (search.google_ticket || search.google_error) void navigate({ to: '/onboarding', search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- פעם אחת, בחזרה מ-Google
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [step]);

  const chooseLanguage = (l: Locale) => {
    setLocale(l, { reload: false });
    setLanguage(l);
    setStep('business');
  };

  const firstName = ownerName.trim().split(/\s+/)[0] ?? '';

  return (
    <div className="flex h-dvh flex-col bg-canvas">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between px-4 py-3">
        <Logo />
        <ThemeToggle />
      </header>

      <main className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col px-4 pb-4">
        <section
          aria-label={t('chatSignup.title')}
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-(--radius-lg) border border-border bg-surface"
        >
          <div role="log" aria-live="polite" className="flex-1 space-y-3 overflow-y-auto p-4">
            <Assistant>
              {googleError && !returned ? (
                <p role="alert" className="mb-1.5 text-danger">
                  {googleError}
                </p>
              ) : null}
              <p>{t('chatSignup.hello')}</p>
              {step === 'language' ? (
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {LOCALES.map((l) => (
                    <button
                      key={l}
                      type="button"
                      lang={l}
                      onClick={() => chooseLanguage(l)}
                      className="inline-flex h-10 items-center rounded-full border border-border bg-surface-raised px-4 text-sm font-medium text-fg hover:border-accent-border hover:bg-accent-subtle/50"
                    >
                      {t(`common:language.${l}`)}
                    </button>
                  ))}
                </div>
              ) : null}
            </Assistant>
            {language ? <Mine>{t(`common:language.${language}`)}</Mine> : null}

            {step !== 'language' ? <Assistant>{t('chatSignup.askBusiness')}</Assistant> : null}
            {step === 'owner' || step === 'account' ? <Mine>{businessName}</Mine> : null}

            {step === 'owner' || step === 'account' ? (
              <Assistant>{t('chatSignup.askOwner', { business: businessName })}</Assistant>
            ) : null}
            {step === 'account' ? <Mine>{ownerName}</Mine> : null}

            {step === 'account' ? (
              <Assistant>
                <p>{t('chatSignup.askAccount', { name: firstName })}</p>
                <AccountCard
                  businessName={businessName}
                  ownerName={ownerName}
                  googleTicket={googleTicket}
                  initialError={googleError}
                />
              </Assistant>
            ) : null}
            <div ref={bottom} />
          </div>

          {step === 'business' ? (
            <ChatInput
              key="business"
              label={t('chatSignup.businessAria')}
              placeholder={t('chatSignup.businessPlaceholder')}
              validate={(v) => (v.length < 2 ? t('signup.errors.businessNameShort') : null)}
              onSubmit={(v) => {
                setBusinessName(v);
                setStep('owner');
              }}
            />
          ) : step === 'owner' ? (
            <ChatInput
              key="owner"
              label={t('chatSignup.ownerAria')}
              placeholder={t('chatSignup.ownerPlaceholder')}
              validate={(v) => (v.length < 1 ? t('signup.errors.nameRequired') : null)}
              onSubmit={(v) => {
                setOwnerName(v);
                setStep('account');
              }}
            />
          ) : null}
        </section>

        <p className="mt-3 text-center text-xs text-fg-muted">
          {t('wizard.alreadyRegistered')}{' '}
          <Link to="/login" className="font-medium text-accent hover:underline">
            {t('wizard.login')}
          </Link>
        </p>
      </main>
    </div>
  );
}

function Assistant({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-accent-subtle text-accent">
        <Bot className="size-4" aria-hidden />
      </span>
      <div className="max-w-[85%] rounded-(--radius-lg) bg-surface-sunken px-3 py-2 text-sm text-fg">
        {children}
      </div>
    </div>
  );
}

function Mine({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-(--radius-lg) bg-accent px-3 py-2 text-sm whitespace-pre-wrap text-fg-on-accent">
        {children}
      </div>
    </div>
  );
}

/** שורת כתיבה עם מיקרופון (זיהוי דיבור של הדפדפן, כשיש). */
function ChatInput({
  label,
  placeholder,
  validate,
  onSubmit,
}: {
  label: string;
  placeholder: string;
  validate: (value: string) => string | null;
  onSubmit: (value: string) => void;
}) {
  const { t } = useTranslation(['onboarding', 'common']);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const speech = useSpeechInput(currentLocale(), (text) => {
    setValue(text);
    setError(null);
  });

  useEffect(() => input.current?.focus(), []);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const v = value.trim();
    const problem = validate(v);
    if (problem) {
      setError(problem);
      return;
    }
    onSubmit(v.slice(0, 120));
  };

  return (
    <form onSubmit={submit} className="border-t border-border p-3">
      <div className="flex items-center gap-2">
        {speech.supported ? (
          <Button
            type="button"
            size="icon"
            variant={speech.listening ? 'danger' : 'secondary'}
            onClick={speech.listening ? speech.stop : speech.start}
            aria-label={speech.listening ? t('chatSignup.stopVoice') : t('chatSignup.voice')}
            aria-pressed={speech.listening}
          >
            {speech.listening ? <Square aria-hidden /> : <Mic aria-hidden />}
          </Button>
        ) : null}
        <input
          ref={input}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          maxLength={120}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          placeholder={speech.listening ? t('chatSignup.listening') : placeholder}
          className="h-11 min-w-0 flex-1 rounded-(--radius-md) border border-border bg-surface px-3 text-sm text-fg placeholder:text-fg-subtle"
        />
        <Button type="submit" size="icon" aria-label={t('common:actions.send')}>
          <ArrowUp aria-hidden />
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function AccountCard({
  businessName,
  ownerName,
  googleTicket,
  initialError,
}: {
  businessName: string;
  ownerName: string;
  /** חזרה מ-Google עם כרטיס — פותחים את החשבון מיד. */
  googleTicket: string | null;
  initialError: string | null;
}) {
  const { t } = useTranslation('onboarding');
  const { adoptSession } = useAuth();
  const qc = useQueryClient();
  const [withEmail, setWithEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; server?: string }>(
    initialError ? { server: initialError } : {},
  );
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(googleTicket !== null);

  const serverError = (err: unknown) =>
    err instanceof ApiError && err.status === 429
      ? t('signup.tooMany')
      : err instanceof ApiError
        ? err.message
        : t('signup.failed');

  const opened = async (session: Parameters<typeof adoptSession>[0]) => {
    saveDraft(null);
    adoptSession(session);
    await qc.invalidateQueries({ queryKey: ['onboarding-import'] });
  };

  // הכרטיס מ-Google נצרך פעם אחת — גם ב-StrictMode, שמריץ אפקטים פעמיים.
  const exchanged = useRef(false);
  useEffect(() => {
    if (!googleTicket || exchanged.current) return;
    exchanged.current = true;
    setupApi
      .googleSignup({ ticket: googleTicket, businessName, ownerName, locale: currentLocale() })
      .then(opened)
      .catch((err: unknown) => {
        setErrors({ server: serverError(err) });
        setGoogleBusy(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- פעם אחת, עם הכרטיס שחזר
  }, [googleTicket]);

  const startGoogle = async () => {
    setErrors({});
    setGoogleBusy(true);
    try {
      saveDraft({ businessName, ownerName, language: currentLocale() });
      const { authUrl } = await setupApi.googleSignupStart();
      window.location.assign(authUrl);
    } catch (err) {
      setErrors({
        server: err instanceof ApiError && err.status === 503 ? t('chatSignup.googleOff') : serverError(err),
      });
      setGoogleBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = t('signup.errors.emailInvalid');
    if (!PASSWORD_RULE.test(password)) next.password = t('signup.errors.passwordRule');
    setErrors(next);
    if (next.email || next.password) return;

    setBusy(true);
    try {
      // התחום מתחיל "אחר"; העוזר מזהה את התחום האמיתי מהמסמכים ומציע לעדכן.
      await opened(
        await setupApi.signup({
          businessName,
          ownerName,
          email: email.trim(),
          password,
          trade: 'OTHER',
          locale: currentLocale(),
        }),
      );
    } catch (err) {
      setErrors({ server: serverError(err) });
      setBusy(false);
    }
  };

  const consent = (
    <p className={cn('text-2xs text-fg-subtle')}>
      <Trans
        t={t}
        i18nKey="chatSignup.consent"
        components={{
          terms: <Link to="/terms" target="_blank" className="text-accent hover:underline" />,
          privacy: <Link to="/privacy" target="_blank" className="text-accent hover:underline" />,
        }}
      />
    </p>
  );

  if (!withEmail) {
    return (
      <div className="mt-2.5 space-y-3 rounded-(--radius-md) border border-border bg-surface p-3">
        <GoogleButton
          busy={googleBusy}
          label={googleBusy ? t('chatSignup.googleOpening') : t('chatSignup.google')}
          onClick={() => void startGoogle()}
        />
        {errors.server ? (
          <p role="alert" className="text-xs text-danger">
            {errors.server}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setErrors({});
            setWithEmail(true);
          }}
          className="block w-full text-center text-xs font-medium text-accent hover:underline"
        >
          {t('chatSignup.withEmail')}
        </button>
        {consent}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      noValidate
      className="mt-2.5 space-y-3 rounded-(--radius-md) border border-border bg-surface p-3"
    >
      <Field
        label={t('signup.email')}
        htmlFor="signup-email"
        hint={t('signup.emailHint')}
        error={errors.email}
        required
      >
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          dir="ltr"
          value={email}
          invalid={!!errors.email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field
        label={t('signup.password')}
        htmlFor="signup-password"
        hint={t('signup.passwordHint')}
        error={errors.password}
        required
      >
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          dir="ltr"
          value={password}
          invalid={!!errors.password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {errors.server ? (
        <p role="alert" className="text-xs text-danger">
          {errors.server}
        </p>
      ) : null}
      <Button type="submit" className="w-full" loading={busy}>
        {t('chatSignup.create')}
      </Button>
      {consent}
    </form>
  );
}
